import {createSlice, createSelector} from '@reduxjs/toolkit'
import {MC_2_0_PLANS} from '../utils/constants'
import {THRYV_BASIC_CODES} from '../utils/tgp-catalog-flags'
import {buildItemsFromRecurly} from '../features/CatalogGrid/utils/mapRecurlySubscriptionsToItems'
import {makeProductGroupSelectors} from './shared/productConfigSelectors'

const initialState = {
  // All products in the invetory will be loaded here
  productConfigInfo: {},
  // Recurly account information will be loaded here
  recurlyAccountInfo: {},
  // Tracks whether the proposal has unsaved cart changes (add/remove items)
  isDirty: false,
  // Tracks which proposal option tab is currently active
  activeOptionDisplayOrder: null,
  // Option selected by the client on the proposal view page
  selectedOptionId: null,
  // flag to check if schedule Required
  isScheduleRequired: false,
  // Original proposal options as fetched from DB — never mutated by Recurly overwrites
  originalOptions: [],
  // Enable_BNI_Mktg_Svcs feature flag (sourced from useUserFeatures() by a
  // component and mirrored here since buildItemsFromRecurly runs inside a
  // plain reducer, which has no access to React context). Defaults false —
  // mirrors screens/catalog.js:1680's `if (!Enable_BNI_Mktg_Svcs)` strip,
  // where an unloaded/absent flag reads falsy too.
  isBniMktgSvcsEnabled: false,
  // Full shape of a populated proposalInfo.
  //
  // Legend:
  //   [required]   always sent in the API request body
  //   [optional]   conditionally sent (omitted when null/falsy)
  //   [read-only]  returned by the API; never sent in the request body
  //   [UI-only]    lives in Redux only; stripped before every API call
  //
  // proposalInfo: {
  //   proposalId:     string | null,   // [read-only]  UUID assigned by backend on POST /proposals
  //   salesRepId:     string | null,   // [required]   sent as `repId` on create; sourced from user.email
  //   userRole:       string | null,   // [required]   on create; effective role of the logged-in user
  //   businessName:   string | null,   // [required]   on create; [optional] on update
  //   clientFirstName: string | null,  // [optional]   client's first name
  //   clientLastName:  string | null,  // [optional]   client's last name
  //   clientEmail:    string | null,   // [optional]   omitted when falsy
  //   clientPhone:    string | null,   // [optional]   omitted when falsy
  //   country:        string | null,   // [required]   on create; [optional] on update — ISO code, e.g. 'US', 'AU'
  //   clientType:     'NEW' | 'EXISTING' | null,  // [required] on create; [optional] on update
  //   thryvId:        string | null,   // [optional]   omitted when falsy; only meaningful when clientType === 'EXISTING'
  //   statusCode:     'DRAFT' | 'SENT' | 'ACCEPTED' | 'EXPIRED' | 'DECLINED',  // [optional] on update only
  //   expirationDate: string | null,   // [optional]   ISO date string; omitted when falsy
  //   sentAt:         string | null,   // [optional]   ISO date string; set when proposal is sent; update only
  //   acceptedAt:     string | null,   // [optional]   ISO date string; set when client accepts; update only
  //   declinedAt:     string | null,   // [optional]   ISO date string; set when client declines; update only
  //   feedback:       string | null,   // [optional]   client decline reason text; update only
  //   isDeleted:      0 | 1,           // [UI-only]    soft-delete flag returned by API; never sent back
  //   communicationMode: eum ['email', 'sms'] // default value as email
  //   options: [                       // [optional]   omitted when empty
  //     {
  //       optionId:      string | undefined,  // [read-only]  UUID from backend; absent before first save
  //       optionName:    string,              // [required]   'Good' | 'Better' | 'Best' or custom label
  //       displayOrder:  number,              // [required]   1-based; middle card is highlighted as featured in preview
  //       dueTodayTotal: number,              // [read-only]  backend-computed sum of all items' dueTodayAmount
  //       monthlyTotal:  number,              // [read-only]  backend-computed sum of all items' recurringAmount
  //       coupons:       string[],            // [optional]   manual coupon codes applied to this option
  //       isSent:  boolean,                   // [optional]  to show whether the option sent to client or not
  //       isSelected:  boolean,               // [optional]  to show which proposal option was selected by client
  //       items: [                            // [optional]   omitted when empty
  //         {
  //           productCode:      string,       // [required]   Recurly plan code, e.g. 'mc_plus-5-thryv-m2m'
  //           productName:      string,       // [required]
  //           productType:      'CENTER' | 'APP' | 'ONE_TIME_FEE',  // [required]
  //           displayOrder:     number,       // [optional]   omitted when absent
  //           quantity:         number,       // [required]   for stepper-driven addons; omitted when absent
  //           unitPrice:        number,       // [required]   base price per unit before quantity; omitted when absent
  //           dueTodayAmount:   number,       // [optional]   one-time charge due at signing; omitted when absent
  //           billingFrequency: 'monthly' | 'annual',  // [required] omitted when absent
  //
  //           // [optional] product-specific config; omitted when absent. Shape varies by product:
  //           //   - plain object  when set via builder config form
  //           //   - JSON string   when fetched back from the API
  //           //   - {name, value}[] when mapped from Recurly subscription customFields
  //           //   Known keys: domain, onboarding, eaid, targetUrl, location, budget, phoneNumber
  //           configuration: object | string | {name: string, value: string}[] | null,
  //
  //           alreadyIncluded: boolean,       // [UI-only]    true for items seeded from an existing Recurly
  //                                           //              subscription; stripped by useProposalSave before save
  //
  //           addons: [                       // [optional]   omitted when empty
  //             {
  //               productCode:      string,   // [required]
  //               productName:      string,   // [required]
  //               productType:      'APP',    // [required]
  //               quantity:         number,   // [required]   omitted when absent
  //               unitPrice:        number,   // [required]   omitted when absent
  //               dueTodayAmount:   number,   // [optional]   omitted when absent
  //               billingFrequency: 'monthly' | 'annual',  // [required] omitted when absent
  //               configuration:    object,   // [optional]   omitted when absent
  //               displayOrder:     number,   // [optional]   omitted when absent
  //             }
  //           ],
  //         }
  //       ],
  //     }
  //   ],
  // }
  //
  // Below JSON represents the current state of the proposal
  proposalInfo: {
    proposalId: null,
    proposalLink: null,
    salesRepId: null,
    userRole: null,
    businessName: null,
    clientFirstName: null,
    clientLastName: null,
    clientEmail: null,
    clientPhone: null,
    country: null,
    clientType: null,
    thryvId: null,
    statusCode: 'DRAFT',
    salesRepOrPartner: 'no',
    salesRepInfo: null,
    xpCode: null,
    overrideSST: 'no',
    overrideRepInfo: null,
    expirationDate: null,
    sentAt: null,
    acceptedAt: null,
    declinedAt: null,
    feedback: null,
    isDeleted: 0,
    communicationMedium: 'email',
    abn: null,
    options: [],
  },
}

const proposalSlice = createSlice({
  name: 'proposal',
  initialState,
  reducers: {
    setProposalInfo(state, action) {
      state.proposalInfo = action.payload
      state.isDirty = false
      state.originalOptions = (action.payload.options || []).map(opt => ({
        ...opt,
        items: (opt.items || []).map(item => ({...item})),
      }))
    },

    updateProposalInfo(state, action) {
      state.proposalInfo = {...state.proposalInfo, ...action.payload}
    },

    setSchdulerRequirementStatus(state, action) {
      state.isScheduleRequired = action.payload
    },

    setBniMktgSvcsEnabled(state, action) {
      state.isBniMktgSvcsEnabled = action.payload
    },

    resetProposalInfo(state) {
      state.proposalInfo = {
        ...initialState.proposalInfo,
        salesRepId: state.proposalInfo.salesRepId,
        userRole: state.proposalInfo.userRole,
      }
    },

    setProductConfigInfo(state, action) {
      state.productConfigInfo = action.payload
    },

    updateProductConfigInfo(state, action) {
      state.productConfigInfo = {...state.productConfigInfo, ...action.payload}
    },

    resetProductConfigInfo(state) {
      state.productConfigInfo = initialState.productConfigInfo
    },

    // Once Recurly account info is loaded, derive its active subscription
    // plans and merge them into every existing proposal option's items.
    // Items already present on the option by productCode are marked
    // alreadyIncluded (alreadyIncluded is stripped before saving, so fetched
    // proposal items never carry it). Items not yet on the option are appended.
    // If the account has no Recurly info/subscriptions, leave options as-is.
    setRecurlyAccountInfo(state, action) {
      state.recurlyAccountInfo = action.payload

      const items = buildItemsFromRecurly(
        action.payload,
        state.productConfigInfo,
        {isBniMktgSvcsEnabled: state.isBniMktgSvcsEnabled},
      )

      if (items.length) {
        const recurlyCodes = new Set(items.map(i => i.productCode))

        // Build planCode → uigroup lookup so we can detect when the sales rep
        // has replaced a Recurly plan with a different plan in the same group.
        const {
          centers = [],
          apps = [],
          oneTimeFees = [],
        } = state.productConfigInfo ?? {}
        const uigroupByCode = new Map(
          [...centers, ...apps, ...oneTimeFees].map(p => [p.code, p.uigroup]),
        )

        state.proposalInfo.options.forEach(option => {
          const existingCodes = new Set(
            (option.items ?? []).map(i => i.productCode),
          )
          // Mark existing items that the client already owns in Recurly.
          // Also transfer recurlyQuantity from the Recurly item so that
          // products like seobase-5-thryv-bni-nt-m2m (where quantity encodes
          // the dollar amount) carry the correct value even when the saved
          // proposal item was stripped of recurlyQuantity by toApiItem.
          const recurlyItemByCode = new Map(items.map(i => [i.productCode, i]))
          option.items = (option.items ?? []).map(item => {
            if (!recurlyCodes.has(item.productCode)) return item
            const recurlyItem = recurlyItemByCode.get(item.productCode)
            return {
              ...item,
              alreadyIncluded: true,
              ...(recurlyItem?.recurlyQuantity != null && {
                recurlyQuantity: recurlyItem.recurlyQuantity,
              }),
            }
          })
          // uigroups already occupied by a newly-added (non-Recurly) item —
          // i.e. the sales rep replaced the old plan with a new one in that group.
          const newItemCodes = (option.items ?? [])
            .filter(i => !recurlyCodes.has(i.productCode))
            .map(i => i.productCode)
          const replacedGroups = new Set(
            newItemCodes.map(code => uigroupByCode.get(code)).filter(Boolean),
          )

          // All MC plan families are mutually exclusive regardless of uigroup.
          // If any MC plan is newly added, suppress every other MC variant
          // from being re-appended from Recurly.
          const ALL_MC_CODES = new Set([
            'mc_plus-5-thryv-m2m',
            'mc_pro-5-thryv-m2m',
            'mc_pro_enh-5-thryv-m2m',
            'mc_foundation-5-thryv-m2m',
            ...THRYV_BASIC_CODES,
            ...MC_2_0_PLANS,
          ])
          const hasNewMCPlan = newItemCodes.some(code => ALL_MC_CODES.has(code))

          // Append Recurly items not already in the option, skipping those
          // whose uigroup was taken over by a replacement plan, and skipping
          // MC displacement.
          const newItems = items.filter(i => {
            if (existingCodes.has(i.productCode)) return false
            if (replacedGroups.has(uigroupByCode.get(i.productCode)))
              return false
            // Suppress old MC plan if any new MC plan is in the option
            if (hasNewMCPlan && ALL_MC_CODES.has(i.productCode)) return false
            return true
          })
          option.items = [...option.items, ...newItems]
        })
      }
    },

    updateRecurlyAccountInfo(state, action) {
      state.recurlyAccountInfo = {
        ...state.recurlyAccountInfo,
        ...action.payload,
      }
    },

    resetRecurlyAccountInfo(state) {
      state.recurlyAccountInfo = initialState.recurlyAccountInfo
    },

    resetAll(state) {
      return {
        ...initialState,
        proposalInfo: {
          ...initialState.proposalInfo,
          salesRepId: state.proposalInfo.salesRepId,
          userRole: state.proposalInfo.userRole,
        },
      }
    },

    // Add a new proposal option. If the account has existing Recurly
    // subscriptions, seed the new option's items with those instead of
    // starting empty.
    addOption(state, action) {
      const recurlyItems = buildItemsFromRecurly(
        state.recurlyAccountInfo,
        state.productConfigInfo,
        {isBniMktgSvcsEnabled: state.isBniMktgSvcsEnabled},
      )
      const items = recurlyItems.length ? recurlyItems : action.payload.items

      state.proposalInfo.options.push({...action.payload, items})

      state.proposalInfo.options.forEach((option, index) => {
        option.displayOrder = index + 1
      })
    },

    // Update an existing proposal option
    updateOption(state, action) {
      const {displayOrder, changes, skipDirty} = action.payload

      const option = state.proposalInfo.options.find(
        option => option.displayOrder === displayOrder,
      )

      if (option) {
        Object.assign(option, changes)
        if (!skipDirty) state.isDirty = true
      }
    },

    resetDirty(state) {
      state.isDirty = false
    },

    // Remove a proposal option
    removeOption(state, action) {
      const displayOrder = action.payload

      state.proposalInfo.options = state.proposalInfo.options.filter(
        option => option.displayOrder !== displayOrder,
      )

      state.proposalInfo.options.forEach((option, index) => {
        option.displayOrder = index + 1
      })
    },

    setActiveOptionDisplayOrder(state, action) {
      state.activeOptionDisplayOrder = action.payload
    },

    setSelectedOptionId(state, action) {
      state.selectedOptionId = action.payload
    },

    // Reset a single option's items back to the account's current Recurly
    // subscription items.
    setOptionItemsFromRecurly(state, action) {
      const displayOrder = action.payload

      const option = state.proposalInfo.options.find(
        option => option.displayOrder === displayOrder,
      )
      if (!option) return

      option.items = buildItemsFromRecurly(
        state.recurlyAccountInfo,
        state.productConfigInfo,
        {isBniMktgSvcsEnabled: state.isBniMktgSvcsEnabled},
      )
    },
  },
})

export const {
  setProposalInfo,
  updateProposalInfo,
  resetProposalInfo,
  addOption,
  updateOption,
  removeOption,
  resetDirty,
  setActiveOptionDisplayOrder,
  setSelectedOptionId,
  setOptionItemsFromRecurly,
  setProductConfigInfo,
  updateProductConfigInfo,
  resetProductConfigInfo,
  setRecurlyAccountInfo,
  updateRecurlyAccountInfo,
  resetRecurlyAccountInfo,
  setSchdulerRequirementStatus,
  setBniMktgSvcsEnabled,
  resetAll,
} = proposalSlice.actions

export default proposalSlice.reducer

/* -------------------------------------------------------------------------- */
/* Selectors */
/* -------------------------------------------------------------------------- */

export const selectProductConfigInfo = state => state.proposal.productConfigInfo

export const {
  selectProductGroups,
  selectSoftwareGroups,
  selectAddonGroups,
  selectIsCatalogDataLoaded,
} = makeProductGroupSelectors(selectProductConfigInfo)

export const selectSchedulerRequirementStatus = state =>
  state.proposal.isScheduleRequired

export const selectOriginalOptions = state => state.proposal.originalOptions

export const selectProposalInfo = state => state.proposal.proposalInfo

export const selectProposalCountry = state =>
  state.proposal.proposalInfo.country

export const selectProposalOptions = createSelector(
  [selectProposalInfo],
  proposal => proposal.options,
)

export const selectRecurlyAccountInfo = state =>
  state.proposal.recurlyAccountInfo

export const selectIsBniMktgSvcsEnabled = state =>
  state.proposal.isBniMktgSvcsEnabled

export const selectRecurlySubscriptionItems = createSelector(
  [
    selectRecurlyAccountInfo,
    selectProductConfigInfo,
    selectIsBniMktgSvcsEnabled,
  ],
  (recurlyAccountInfo, productConfigInfo, isBniMktgSvcsEnabled) =>
    buildItemsFromRecurly(recurlyAccountInfo, productConfigInfo, {
      isBniMktgSvcsEnabled,
    }),
)

export const selectIsDirty = state => state.proposal.isDirty

export const selectActiveOptionDisplayOrder = state =>
  state.proposal.activeOptionDisplayOrder

export const selectSelectedOptionId = state => state.proposal.selectedOptionId
export const selectActiveOption = createSelector(
  [selectProposalOptions, selectActiveOptionDisplayOrder],
  (options, activeDisplayOrder) =>
    options.find(o => o.displayOrder === activeDisplayOrder) ?? null,
)

export const selectOneTimeFeeDefinitions = state =>
  state.proposal.productConfigInfo?.oneTimeFees ?? []
